// ============================================================
// components/common/Reveal.tsx
//
// Scroll reveal: children start slightly scaled down and
// transparent, then pop out from their center the first time
// they scroll into view. Content is never left hidden: without
// IntersectionObserver it renders visible immediately, and
// reduced-motion users get no transform or fade.
// ============================================================
import React, { useEffect, useRef, useState } from 'react';

interface RevealProps {
  children: React.ReactNode;
  className?: string;
}

export function Reveal({ children, className = '' }: RevealProps): React.JSX.Element {
  const ref = useRef<HTMLDivElement>(null);
  const [revealed, setRevealed] = useState(() => typeof IntersectionObserver === 'undefined');

  useEffect(() => {
    const el = ref.current;
    if (revealed || !el || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setRevealed(true);
          observer.disconnect();
        }
      },
      // Fire when the section's top edge crosses 90% of the viewport height.
      // threshold 0 (not a ratio) so a section of any height can trigger.
      { threshold: 0, rootMargin: '0px 0px -10% 0px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [revealed]);

  return (
    <div
      ref={ref}
      data-revealed={revealed ? 'true' : 'false'}
      className={[
        'origin-center transition-[opacity,scale] duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]',
        revealed ? 'scale-100 opacity-100' : 'scale-[0.92] opacity-0',
        'motion-reduce:scale-100 motion-reduce:opacity-100 motion-reduce:transition-none',
        // Never print a section that hadn't scrolled into view yet.
        'print:scale-100 print:opacity-100',
        className,
      ].join(' ')}
    >
      {children}
    </div>
  );
}
